import anime from 'animejs/lib/anime.es.js'
import Lenis from '@studio-freight/lenis'
import { SubMenu } from '@/components/SubMenu.js'
import { Layout } from '@/core/Layout.js'
import { Raf } from '@/core/Raf.js'
import { Sleep } from '@/utils/Sleep.js'
import { processUrl } from '@/utils/Url.js'
import { NavSearch } from '@/components/NavSearch.js'
import { ensureTheatreProject } from './TheatreProject.js'

export class Nav {
  static init() {
    this.menuOpen = false

    this.id = 'nav'

    this.lastScroll = 0

    this.el = document.querySelector('.c-nav')
    this.lang = document.querySelector('.c-nav-bar-lang')
    this.submenus = document.querySelectorAll('.c-nav-bar-menu-li')
    this.menuToggle = document.querySelector('.c-nav-bar-menu-toggle')
    this.closeMenuButton = document.querySelector('.c-nav-menu-close')
    this.navSiteLinks = document.querySelectorAll('.c-nav-bar-lang-a')
    this.scrollToLinks = this.el.querySelectorAll('[data-scroll-to], a[href^="#"]')

    this.menuDarkIllustration = null
    this.menuLightIllustration = null
    this.menuIllustrationsReady = false

    this.toggleMenu = this.toggleMenu.bind(this)
    this.closeMenu = this.closeMenu.bind(this)
    this.onCloseButton = this.onCloseButton.bind(this)
    this.onRaf = this.onRaf.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onScrollTo = this.onScrollTo.bind(this)

    // this.scrollTo = new ScrollTo('[data-scroll-to]', this.el, 'ScrollTo')
    this.navSearch = new NavSearch(this.el.querySelector('.c-nav-search'))

    this.submenus.forEach((submenu) => {
      new SubMenu(submenu)
    })

    this.addEvents()
  }

  static addEvents() {
    this.lang.addEventListener('click', this.toggleLang.bind(this))

    document.addEventListener('click', this.onDocumentClick.bind(this))
    document.addEventListener('scroll', this.onScroll.bind(this))

    this.menuToggle.addEventListener('click', this.toggleMenu)
    this.closeMenuButton.addEventListener('click', this.onCloseButton)
    this.navSiteLinks.forEach((siteLink) => siteLink.addEventListener('click', this.onSiteRedirect.bind(this)))
    this.scrollToLinks.forEach((link) => link.addEventListener('click', this.onScrollTo))
  }

  static destroy() {
    this.lang.removeEventListener('click', this.toggleLang.bind(this))

    document.removeEventListener('click', this.onDocumentClick.bind(this))
    document.removeEventListener('scroll', this.onScroll.bind(this))

    this.menuToggle.removeEventListener('click', this.toggleMenu)
    this.closeMenuButton.removeEventListener('click', this.onCloseButton)
  }

  static onKeyDown(e) {
    if (e.keyCode === 27) this.closeMenu()
  }

  static async onScrollTo(e) {
    const link = e.currentTarget
    if (!link) return

    const isObfuscatedLink = link.dataset.olink !== undefined
    if (isObfuscatedLink) return

    const href = link.getAttribute('href')
    if (!href) return

    const processedUrl = processUrl(link.href)
    const currentUrl = processUrl(window.location.href)
    const isSamePageAnchor = processedUrl.hasHash && processedUrl.href === currentUrl.href

    // Allow default navigation for cross-page links.
    if (!isSamePageAnchor) return

    const scrollTarget = document.querySelector(processedUrl.hash)
    if (!scrollTarget) return

    e.preventDefault()
    this.closeMenu()

    await Sleep(100)

    let scrollDuration = 1.2
    const scrollDistance = scrollTarget.offsetTop - window.scrollY
    scrollDuration = Math.min((scrollDistance / window.innerHeight) * 0.25, 4.2)
    scrollDuration = Math.max(1.2, scrollDuration)
    Layout.scroll.scrollTo(scrollTarget, { force: true, duration: scrollDuration })
  }

  static onScroll(e) {
    this.lang.classList.remove('is-active')

    if (window.scrollY > this.lastScroll && window.scrollY > 50) {
      this.el.classList.add('is-hidden')
    } else {
      this.el.classList.remove('is-hidden')
    }

    if (window.scrollY > 100) {
      this.el.classList.add('is-scrolled')
    } else {
      this.el.classList.remove('is-scrolled')
    }

    this.lastScroll = window.scrollY
  }

  static onDocumentClick(e) {
    if (!e.target.closest('.c-nav-bar-lang')) {
      this.lang.classList.remove('is-active')
    }
  }

  static toggleMenu(e) {
    e.preventDefault()

    if (this.menuOpen) {
      this.closeMenu()
    } else {
      this.openMenu().catch((error) => {
        console.warn('Failed to open menu animations:', error)
      })
    }
  }

  static toggleLang(e) {
    e.preventDefault()

    if (this.lang.classList.contains('is-active')) {
      this.lang.classList.remove('is-active')
    } else {
      this.lang.classList.add('is-active')
    }
  }

  static updateNavActive() {
    const menuLinks = document.querySelectorAll('.c-nav a')

    menuLinks.forEach((link) => {
      link.classList.remove('is-active')

      const target = processUrl(link.href)
      const currentLocation = processUrl(window.location.href)

      if (currentLocation.raw === target.raw) {
        link.classList.add('is-active')
      }
    })
  }

  static async openMenu() {
    const menu = document.querySelector('.c-nav-menu')
    const navLinks = document.querySelectorAll('.c-nav-menu-content-a-text-static')

    menu.classList.add('is-active')

    await this.ensureMenuIllustrations()

    Layout.scroll.stop()

    this.scroll = new Lenis({
      wrapper: this.el.querySelector('.c-nav-menu-content'),
      content: this.el.querySelector('.c-nav-menu-content-inner'),
    })

    Raf.add(this.onRaf, this.id)
    document.addEventListener('keydown', this.onKeyDown)

    this.closeTl?.pause()

    this.openTl = anime.timeline()

    this.menuDarkIllustration.animateIn()
    this.menuLightIllustration.animateIn()

    this.openTl.add({
      targets: menu,
      translateX: ['102%', '0'],
      duration: 1200,
      easing: 'easeOutExpo',
    })

    this.openTl.add(
      {
        targets: navLinks,
        delay: 0,
        duration: 1200,
        easing: 'easeOutExpo',
        translateY: ['102%', 0],
        opacity: [0, 1],
        delay: anime.stagger(70),
      },
      '0'
    )

    this.openTl.add(
      {
        targets: [document.querySelector('.c-nav-menu-content'), document.querySelector('.c-nav-menu-bg')],
        translateX: ['-50%', '0'],
        duration: 1200,
        easing: 'easeOutExpo',
      },
      '0'
    )
  }

  static async ensureMenuIllustrations() {
    if (this.menuIllustrationsReady) return

    await ensureTheatreProject()

    const [{ IllustrationMenuDark }, { IllustrationMenuLight }] = await Promise.all([
      import('../components/illustrations/IllustrationMenuDark.js'),
      import('../components/illustrations/IllustrationMenuLight.js'),
    ])

    this.menuDarkIllustration = new IllustrationMenuDark('.c-illustration-menu-dark', this.el, 'IllustrationMenuDark', false)
    this.menuLightIllustration = new IllustrationMenuLight('.c-illustration-menu-light', this.el, 'IllustrationMenuLight', false)
    this.menuIllustrationsReady = true
  }

  static onRaf({ frame }) {
    this.scroll.raf(frame)
  }

  static onCloseButton() {
    this.closeMenu()
  }

  static async closeMenu(force = false) {
    const menu = document.querySelector('.c-nav-menu')
    menu.classList.remove('is-active')

    if (Layout.scroll) Layout.scroll.start()
    if (this.scroll) this.scroll.destroy()

    Raf.remove(this.id)
    document.removeEventListener('keydown', this.onKeyDown)

    this.openTl?.pause()

    this.closeTl = anime.timeline()

    this.closeTl.add({
      targets: document.querySelectorAll('.c-nav-menu'),
      translateX: ['0', '102%'],
      duration: force ? 0 : 1200,
      delay: anime.stagger(10),
      easing: 'easeOutExpo',
    })

    this.closeTl.add(
      {
        targets: [document.querySelector('.c-nav-menu-content'), document.querySelector('.c-nav-menu-bg')],
        translateX: ['0', '-50%'],
        duration: force ? 0 : 1200,
        easing: 'easeOutExpo',
      },
      '0'
    )

    this.menuDarkIllustration?.animateOut()
    this.menuLightIllustration?.animateOut()
  }

  static clear() {
    this.el?.classList.remove('is-dark')
    this.el?.classList.remove('is-light')

    this.closeMenu(true)
  }

  static async show(theme = 'dark') {
    this.clear()

    await Sleep(100)

    this.el.classList.add(`is-${theme}`)
  }

  static async onSiteRedirect(e) {
    e.preventDefault()

    const link = e.currentTarget
    if (!link) return
    const siteHandle = link.dataset.siteHandle
    let redirectUrl = `/${siteHandle}/`

    // Route-based pages (e.g. the author page) expose their translated URLs directly,
    // since there is no entry to resolve via the api/site endpoint.
    const siteUrls = document.querySelector('div[data-page][data-site-urls]')?.dataset.siteUrls
    if (siteUrls) {
      try {
        const urls = JSON.parse(siteUrls)
        if (urls[siteHandle]) {
          location.href = urls[siteHandle]
          return
        }
      } catch (err) {}
    }

    const entryId = document.querySelector('div[data-page][data-entry-id]')?.dataset.entryId
    if (entryId) {
      const response = await fetch(`/${siteHandle}/api/site/?entryId=${entryId}`)
      const data = await response.json().catch((e) => {})
      if (data && data.url) redirectUrl = data.url
    }

    location.href = redirectUrl
  }
}
